import ApiClient from './apiClient'

export type PreorderHardwareType = 'cpus' | 'disks' | 'rams'
export type PreorderDiskType = 'HDD' | 'SSD' | 'NVME'

type HardwareFields = {
  name: string;
  model: string;
  description: string;
  is_active: boolean;
}

export type PreorderHardwarePayloads = {
  cpus: HardwareFields & {
    cores: number;
    threads: number;
    speed_mhz: number;
    turbo_speed_mhz: number;
  };
  disks: HardwareFields & { disk_type: PreorderDiskType; size_gb: number };
  rams: HardwareFields & { size_mb: number };
}

export type PreorderHardware = PreorderHardwarePayloads[PreorderHardwareType] & {
  id: string;
  created_at: string;
  updated_at: string;
}

export type PreorderHardwareList = {
  count: number;
  next: string | null;
  previous: string | null;
  results: PreorderHardware[];
}

type PreorderHardwareListResponse = Omit<PreorderHardwareList, 'count' | 'results'> & {
  count?: number;
  totalCount?: number;
  objects?: PreorderHardware[];
  results?: PreorderHardware[];
}

export type PreorderHardwareQueries = {
  page?: number;
  page_size?: number;
  search?: string;
  ordering?: string;
  is_active?: boolean;
}

const resourcePath = (type: PreorderHardwareType) => `dedicated-servers/preorder/${type}`

const preorderHardwareService = {
  list: (type: PreorderHardwareType, params: PreorderHardwareQueries, signal?: AbortSignal) =>
    ApiClient.get<PreorderHardwareListResponse>(resourcePath(type), { params, signal })
      .then((response) => {
        // Live staff API uses objects; OpenAPI also documents results.
        const results = response.data.objects ?? response.data.results ?? []
        return {
          ...response,
          data: {
            ...response.data,
            count: response.data.count ?? response.data.totalCount ?? results.length,
            results,
          },
        }
      }),

  get: (type: PreorderHardwareType, id: string, signal?: AbortSignal) =>
    ApiClient.get<PreorderHardware>(`${resourcePath(type)}/${id}`, { signal }),

  create: <T extends PreorderHardwareType>(type: T, data: PreorderHardwarePayloads[T]) =>
    ApiClient.post<PreorderHardware>(resourcePath(type), data),

  update: <T extends PreorderHardwareType>(type: T, id: string, data: PreorderHardwarePayloads[T]) =>
    ApiClient.patch<PreorderHardware>(`${resourcePath(type)}/${id}`, data),

  delete: (type: PreorderHardwareType, id: string) =>
    ApiClient.delete(`${resourcePath(type)}/${id}`),
}

export default preorderHardwareService
